import { channelMetadata } from "../../channels/metadata";
import type { LedgerType, NavGroup, NavItem, ChannelId } from "./types";

export const channels = channelMetadata as ReadonlyArray<{
  id: ChannelId;
  label: string;
  description: string;
}>;

export type Translator = (key: string, fallback: string) => string;

export const ledgerTypes = [
  { id: "actual", label: "Actual", labelKey: "ledger.actual" },
  { id: "budget", label: "Budget", labelKey: "ledger.budget" },
  { id: "forecast", label: "Forecast", labelKey: "ledger.forecast" },
] as const satisfies ReadonlyArray<{
  id: LedgerType;
  label: string;
  labelKey: string;
}>;

export const logoUrl = "/assets/hytteflow_logo.png";

const bookingsNavItems = (t: Translator): NavItem[] => [
  {
    path: "/dashboard",
    label: t("nav.dashboard", "Dashboard"),
    description: t("nav.dashboardDescription", "Quick overview of what needs attention."),
    icon: "dashboard",
  },
  {
    path: "/bookings",
    label: t("nav.bookings", "Bookings"),
    description: t("nav.bookingsDescription", "See aggregated bookings, weeks, and months."),
    icon: "bookings",
  },
  {
    path: "/statistics",
    label: t("nav.statistics", "Statistics"),
    description: t("nav.statisticsDescription", "Review booking performance and income."),
    icon: "statistics",
  },
  {
    path: "/receivables",
    label: t("nav.receivables", "Receivables"),
    description: t(
      "nav.receivablesDescription",
      "Track channel receivables and settlement status.",
    ),
    icon: "receivables",
  },
  {
    path: "/profit-loss",
    label: t("nav.profitLoss", "Profit & Loss"),
    description: t("nav.profitLossDescription", "Compare actuals, budget, forecast, and last year."),
    icon: "profit-loss",
  },
  {
    path: "/budget",
    label: t("nav.budgetWorksheet", "Budget worksheet"),
    description: t(
      "nav.budgetWorksheetDescription",
      "Edit multi-year budgets in a spreadsheet-style grid.",
    ),
    icon: "budget",
  },
];

const waterElectricityNavItems = (t: Translator): NavItem[] => [
  {
    path: "/meter-readings",
    label: t("nav.meterReadings", "Meter readings"),
    description: t(
      "nav.meterReadingsDescription",
      "Review and validate electricity and water readings.",
    ),
    icon: "meter-readings",
  },
  {
    path: "/consumption",
    label: t("nav.consumption", "Consumption"),
    description: t(
      "nav.consumptionDescription",
      "Analyze electricity and water usage per day.",
    ),
    icon: "consumption",
  },
];

const accountingNavItems = (t: Translator): NavItem[] => [
  {
    path: "/journal-entries",
    label: t("nav.journalEntries", "Journal entries"),
    description: t(
      "nav.journalEntriesDescription",
      "Create and review journal entries, budgets, and forecasts.",
    ),
    icon: "journal-entries",
  },
  {
    path: "/chart-of-accounts",
    label: t("nav.chartOfAccounts", "Chart of accounts"),
    description: t("nav.chartOfAccountsDescription", "Review account structure and balances."),
    icon: "chart-of-accounts",
  },
  {
    path: "/bank-transactions",
    label: t("nav.bankTransactions", "Bank transactions"),
    description: t("nav.bankTransactionsDescription", "Import and review house bank statements."),
    icon: "bank-transactions",
  },
];

const settingsNavItems = (t: Translator): NavItem[] => [
  {
    path: "/notifications",
    label: t("nav.notifications", "Notifications"),
    description: t("nav.notificationsDescription", "Review recent alerts and updates."),
    icon: "notifications",
  },
  {
    path: "/settings",
    label: t("nav.settings", "Settings"),
    description: t("nav.settingsDescription", "Manage houses and access."),
    icon: "settings",
  },
];

const adminNavItems = (t: Translator): NavItem[] => [
  {
    path: "/admin",
    label: t("nav.admin", "Admin"),
    description: t("nav.adminDescription", "Review system-wide stats and activity."),
    icon: "admin",
  },
];

export const buildNavGroups = (t: Translator): NavGroup[] => [
  {
    id: "bookings",
    label: t("navGroup.bookings", "Bookings & reports"),
    items: bookingsNavItems(t),
  },
  {
    id: "water-electricity",
    label: t("navGroup.waterElectricity", "Water & electricity"),
    items: waterElectricityNavItems(t),
  },
  {
    id: "accounting",
    label: t("navGroup.accounting", "Accounting"),
    items: accountingNavItems(t),
  },
  {
    id: "settings",
    label: t("navGroup.settings", "Settings"),
    items: settingsNavItems(t),
  },
  {
    id: "admin",
    label: t("navGroup.admin", "Admin"),
    items: adminNavItems(t),
  },
];

export const buildOverviewNavItems = (groups: NavGroup[]) =>
  groups.flatMap((group) => group.items);
